import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import MascotLoader from '../components/MascotLoader';
import PdfSignPlacer from '../components/PdfSignPlacer';
import PhotoEditor from '../components/PhotoEditor';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ArrowLeft, MapPin, Building, Users, Home, Eye, Camera, Droplets, Wrench, FileText, Calendar, User, Phone, CheckCircle, XCircle, Download, Image, FileDown, Send, PenTool, Mail, Upload, Trash2, FolderOpen, Plus, Edit3, Crop } from 'lucide-react';
import jsPDF from 'jspdf';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO = '/kardif-logo.png';

function SignaturePad({ onSave, title }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#1A1A1A';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height)
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const save = () => {
    const dataUrl = canvasRef.current.toDataURL('image/png');
    onSave(dataUrl);
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-[#6B7280]">{title}</p>
      <canvas
        ref={canvasRef}
        width={400}
        height={200}
        className="border rounded-lg w-full touch-none cursor-crosshair"
        style={{ maxWidth: '100%', height: 'auto', aspectRatio: '2/1' }}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
      />
      <div className="flex gap-2">
        <Button variant="outline" onClick={clear}>Effacer</Button>
        <Button onClick={save} className="bg-[#8B7355] hover:bg-[#7A6548] text-white">Valider signature</Button>
      </div>
    </div>
  );
}

function InterventionDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [rapport, setRapport] = useState('');
  const [showSignature, setShowSignature] = useState(false);
  const [franchise, setFranchise] = useState(false);
  const [franchiseMontant, setFranchiseMontant] = useState('');
  const [rapportFinalFile, setRapportFinalFile] = useState(null);
  const [compteRenduFinal, setCompteRenduFinal] = useState('');
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailCc, setEmailCc] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showTextEdit, setShowTextEdit] = useState(false);
  const [savingText, setSavingText] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [uploadingAttach, setUploadingAttach] = useState(false);
  const [excludedDocIds, setExcludedDocIds] = useState([]);
  const [photoEditor, setPhotoEditor] = useState({ open: false, src: null, onSaveUrl: null });
  const [editorUploading, setEditorUploading] = useState(false);
  const canEditReport = user?.role === 'admin' || user?.role === 'secretary';

  const uploadEditedBlob = async (blob) => {
    const fd = new FormData();
    fd.append('file', new File([blob], `edited_${Date.now()}.jpg`, { type: 'image/jpeg' }));
    const res = await axios.post(API + '/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
    let url = res.data?.url;
    if (url && url.startsWith('/')) url = process.env.REACT_APP_BACKEND_URL + url;
    return url;
  };

  const openPhotoEditor = (src, onSaveUrl) => setPhotoEditor({ open: true, src, onSaveUrl });

  const handlePhotoEditorSave = async (blob) => {
    setEditorUploading(true);
    try {
      const url = await uploadEditedBlob(blob);
      if (!url) { toast.error('Échec de l\'enregistrement'); return; }
      if (photoEditor.onSaveUrl) await photoEditor.onSaveUrl(url);
      setPhotoEditor({ open: false, src: null, onSaveUrl: null });
      toast.success('Photo modifiée');
      await reloadData();
    } catch (e) {
      toast.error('Erreur lors de la modification');
    } finally {
      setEditorUploading(false);
    }
  };

  const editFacade = () => openPhotoEditor(data.photo_facade, async (url) => {
    await axios.put(`${API}/interventions/${id}`, { photo_facade: url });
  });
  const editDesordre = (i) => openPhotoEditor(data.photos_desordres[i], async (url) => {
    const arr = [...(data.photos_desordres || [])]; arr[i] = url;
    await axios.put(`${API}/interventions/${id}`, { photos_desordres: arr });
  });
  const editNestedPhoto = (field, itemObj, photoIdx) => {
    const realIdx = (data[field] || []).indexOf(itemObj);
    const src = itemObj?.photos?.[photoIdx];
    if (!src || realIdx < 0) return;
    openPhotoEditor(src, async (url) => {
      const arr = JSON.parse(JSON.stringify(data[field]));
      arr[realIdx].photos[photoIdx] = url;
      await axios.put(`${API}/interventions/${id}`, { [field]: arr });
    });
  };

  // Construit le corps de la requête de génération avec le choix des PDF à annexer (signés OU non)
  const buildReportBody = () => {
    const docs = (data?.pieces_jointes || []).filter((p) => p.url);
    if (docs.length === 0) return {};
    const includeIds = docs.filter((p) => !excludedDocIds.includes(p.id)).map((p) => p.id);
    return { editor_settings: { include_attachment_ids: includeIds } };
  };

  const reloadData = async () => {
    try { const r = await axios.get(API + '/interventions/' + id); setData(r.data); } catch (e) {}
  };

  const openTextEdit = () => {
    setEditForm({
      resume_constats: data.resume_constats || '',
      origine_probable: data.origine_probable || '',
      preconisations: data.preconisations || '',
      points_urgents: data.points_urgents || '',
      notes_conclusion: data.notes_conclusion || '',
      procedes: (data.procedes || []).map(p => ({ ...p })),
    });
    setShowTextEdit(true);
  };

  const saveText = async () => {
    setSavingText(true);
    try {
      await axios.put(API + '/interventions/' + id, {
        resume_constats: editForm.resume_constats,
        origine_probable: editForm.origine_probable,
        preconisations: editForm.preconisations,
        points_urgents: editForm.points_urgents,
        notes_conclusion: editForm.notes_conclusion,
        procedes: editForm.procedes,
      });
      toast.success('Rapport corrigé. Régénérez le PDF pour voir les corrections.');
      setShowTextEdit(false);
      await reloadData();
    } catch (e) {
      toast.error("Échec de l'enregistrement");
    } finally { setSavingText(false); }
  };

  const uploadAttachment = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf') && file.type !== 'application/pdf') {
      toast.error('Seuls les fichiers PDF sont acceptés');
      return;
    }
    setUploadingAttach(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/interventions/${id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Pièce jointe ajoutée');
      await reloadData();
    } catch (e) {
      toast.error("Échec de l'ajout");
    } finally { setUploadingAttach(false); }
  };

  const deleteAttachment = async (attId) => {
    try {
      await axios.delete(`${API}/interventions/${id}/attachments/${attId}`);
      toast.success('Pièce jointe supprimée');
      await reloadData();
    } catch (e) { toast.error('Échec de la suppression'); }
  };

  const [signAtt, setSignAtt] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [signPlacement, setSignPlacement] = useState({ page: null, x: 0.5, y: 0.8, width_frac: 0.42 });
  const [sigPreview, setSigPreview] = useState(null);
  const [signing, setSigning] = useState(false);
  const sigCanvasRef = useRef(null);
  const sigDrawing = useRef(false);

  const sigStart = (e) => {
    sigDrawing.current = true;
    const c = sigCanvasRef.current; const r = c.getBoundingClientRect();
    const ctx = c.getContext('2d'); ctx.beginPath();
    ctx.moveTo((e.clientX - r.left) * (c.width / r.width), (e.clientY - r.top) * (c.height / r.height));
  };
  const sigMove = (e) => {
    if (!sigDrawing.current) return;
    const c = sigCanvasRef.current; const r = c.getBoundingClientRect();
    const ctx = c.getContext('2d'); ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.strokeStyle = '#111';
    ctx.lineTo((e.clientX - r.left) * (c.width / r.width), (e.clientY - r.top) * (c.height / r.height)); ctx.stroke();
  };
  const sigEnd = () => {
    if (!sigDrawing.current) return;
    sigDrawing.current = false;
    try { setSigPreview(sigCanvasRef.current.toDataURL('image/png')); } catch (e) {}
  };
  const sigClear = () => { const c = sigCanvasRef.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); setSigPreview(null); };

  const submitSignature = async () => {
    const texts = signPlacement.texts || [];
    const drewSig = !!sigPreview;
    if (!drewSig && texts.length === 0) { toast.error('Ajoutez une signature ou un texte'); return; }
    const dataUrl = drewSig ? sigCanvasRef.current.toDataURL('image/png') : null;
    setSigning(true);
    try {
      await axios.post(`${API}/interventions/${id}/attachments/${signAtt.id}/sign`, {
        signature: dataUrl,
        signer_name: signerName,
        page: signPlacement.page,
        x: signPlacement.x,
        y: signPlacement.y,
        width_frac: signPlacement.width_frac,
        texts,
      });
      toast.success(drewSig ? 'PDF signé ajouté aux pièces jointes' : 'PDF annoté ajouté aux pièces jointes');
      setSignAtt(null); setSignerName(''); setSigPreview(null);
      await reloadData();
    } catch (e) { toast.error('Échec de l\'enregistrement'); }
    finally { setSigning(false); }
  };

  // Ouvre/télécharge un fichier stocké (préfixe le backend si URL relative)
  const fileHref = (url) => (!url || url.startsWith('http') || url.startsWith('data:')) ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;


  useEffect(() => {
    axios.get(API + '/interventions/' + id)
      .then(r => { 
        setData(r.data); 
        setRapport(r.data.rapport_final || ''); 
        setFranchise(!!r.data.franchise);
        setFranchiseMontant(r.data.franchise_montant || '');
        setCompteRenduFinal(r.data.compte_rendu_final || '');
      })
      .catch(() => { toast.error('Erreur'); nav(user?.role === 'admin' ? '/admin' : user?.role === 'secretary' ? '/secretary' : '/dashboard'); })
      .finally(() => setLoading(false));
  }, [id, nav, user?.role]);

  const updateStatus = async (s) => {
    setUpdating(true);
    try {
      await axios.put(`${API}/interventions/${id}`, { status: s });
      setData(p => ({ ...p, status: s }));
      toast.success('OK');
    } catch (e) {
      toast.error('Erreur');
    }
    setUpdating(false);
  };

  const saveRapport = async (notifySecretary = false) => {
    setUpdating(true);
    try {
      await axios.put(`${API}/interventions/${id}`, { 
        rapport_final: rapport, 
        status: 'validated',
        franchise: franchise ? 'oui' : null,
        franchise_montant: franchise ? franchiseMontant : null
      });
      setData(p => ({ ...p, rapport_final: rapport, status: 'validated', franchise: franchise ? 'oui' : null, franchise_montant: franchiseMontant }));
      
      if (notifySecretary) {
        // Create notification for secretary
        await axios.post(`${API}/notifications`, {
          type: 'rapport_final_ready',
          intervention_id: id,
          message: `Rapport final prêt - ${data.adresse_complete}`
        }).catch(() => {});
      }
      
      toast.success('Rapport enregistré' + (notifySecretary ? ' - Secrétaire notifiée' : ''));
    } catch (e) {
      toast.error('Erreur');
    }
    setUpdating(false);
  };

  const handleSignature = async (signatureData) => {
    setUpdating(true);
    try {
      const formData = new FormData();
      formData.append('client_signature', signatureData);
      await axios.post(`${API}/interventions/${id}/sign`, formData);
      setData(p => ({ ...p, client_signature: signatureData, signed_at: new Date().toISOString() }));
      setShowSignature(false);
      toast.success('Signature enregistrée');
    } catch (e) {
      toast.error('Erreur');
    }
    setUpdating(false);
  };

  const sendEmail = async () => {
    try {
      const res = await axios.post(`${API}/interventions/${id}/send-email`);
      if (res.data.configured === false) {
        toast.warning('SMTP non configuré. Configurez les identifiants email dans les paramètres.');
      } else {
        toast.success('Email envoyé à ' + res.data.recipients.join(', '));
      }
    } catch (e) {
      toast.error('Erreur envoi email');
    }
  };

  // Aperçu du compte-rendu PDF (ouvre dans un nouvel onglet, sans télécharger)
  const previewReport = async () => {
    const popup = window.open('about:blank', '_blank');
    setExporting(true);
    try {
      const response = await axios.post(`${API}/interventions/${id}/generate-report?format=pdf`, buildReportBody(), {
        responseType: 'blob'
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      if (popup) {
        popup.location.replace(blobUrl);
      } else {
        const a = document.createElement('a');
        a.href = blobUrl; a.target = '_blank'; a.rel = 'noopener';
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      console.error(e);
      if (popup) popup.close();
      toast.error('Erreur lors de la génération de l\'aperçu');
    } finally {
      setExporting(false);
    }
  };

  // Télécharger compte-rendu PDF ou Word
  const downloadReport = async (format) => {
    try {
      toast.info(`Génération du ${format.toUpperCase()} en cours...`);
      const response = await axios.post(`${API}/interventions/${id}/generate-report?format=${format}`, buildReportBody(), {
        responseType: 'blob'
      });
      
      const filename = format === 'docx' 
        ? `compte_rendu_${id.substring(0, 8)}.docx`
        : `compte_rendu_${id.substring(0, 8)}.pdf`;
      
      saveAs(response.data, filename);
      toast.success(`${format.toUpperCase()} téléchargé !`);
    } catch (e) {
      console.error(e);
      toast.error(`Erreur génération ${format.toUpperCase()}`);
    }
  };

  // Envoyer le rapport par email
  const sendReportByEmail = async () => {
    if (!emailTo.trim()) {
      toast.error('Veuillez saisir une adresse email');
      return;
    }
    
    setSendingEmail(true);
    try {
      const ccEmails = emailCc.trim() ? emailCc.split(',').map(e => e.trim()).filter(e => e) : null;
      
      await axios.post(`${API}/interventions/${id}/send-report`, {
        to_email: emailTo.trim(),
        cc_emails: ccEmails
      });
      
      toast.success(`Rapport envoyé à ${emailTo} !`);
      setShowEmailDialog(false);
      setEmailTo('');
      setEmailCc('');
      // Rafraîchir pour mettre à jour l'historique des envois
      try {
        const r = await axios.get(API + '/interventions/' + id);
        setData(r.data);
      } catch (_) {}
    } catch (e) {
      console.error(e);
      const errorMsg = e.response?.data?.detail || 'Erreur envoi email';
      toast.error(errorMsg);
    } finally {
      setSendingEmail(false);
    }
  };

  // Upload document final
  const uploadDocument = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploadingDoc(true);
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await axios.post(`${API}/interventions/${id}/documents`, formData);
        setData(prev => ({
          ...prev,
          documents_finaux: [...(prev.documents_finaux || []), res.data.document]
        }));
        toast.success(`${file.name} ajouté`);
      } catch (e) {
        toast.error(`Erreur upload ${file.name}`);
      }
    }
    setUploadingDoc(false);
  };

  // Upload photo finale
  const uploadPhotoFinale = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploadingPhoto(true);
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await axios.post(`${API}/interventions/${id}/photos-finales`, formData);
        setData(prev => ({
          ...prev,
          photos_finales: [...(prev.photos_finales || []), res.data.url]
        }));
        toast.success(`Photo ajoutée`);
      } catch (e) {
        toast.error(`Erreur upload photo`);
      }
    }
    setUploadingPhoto(false);
  };

  // Remove document
  const removeDocument = async (index) => {
    try {
      await axios.delete(`${API}/interventions/${id}/documents/${index}`);
      setData(prev => ({
        ...prev,
        documents_finaux: prev.documents_finaux.filter((_, i) => i !== index)
      }));
      toast.success('Document supprimé');
    } catch (e) {
      toast.error('Erreur suppression');
    }
  };

  // Save compte-rendu
  const saveCompteRendu = async (notify = false) => {
    setUpdating(true);
    try {
      await axios.put(`${API}/interventions/${id}/compte-rendu`, {
        compte_rendu_final: compteRenduFinal,
        notify_secretary: notify
      });
      setData(prev => ({ ...prev, compte_rendu_final: compteRenduFinal }));
      toast.success('Compte-rendu enregistré' + (notify ? ' - Secrétaire notifiée' : ''));
    } catch (e) {
      toast.error('Erreur');
    }
    setUpdating(false);
  };

  const exportPDF = async () => {
    setExporting(true);
    toast.info('Génération PDF...');
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 15;
      let y = 20;

      pdf.setFontSize(20);
      pdf.setTextColor(139, 115, 85);
      pdf.text('KARDIF Investigation', pageWidth / 2, y, { align: 'center' });
      y += 8;
      pdf.setFontSize(10);
      pdf.setTextColor(107, 114, 128);
      pdf.text('La solution après sinistre', pageWidth / 2, y, { align: 'center' });
      y += 15;

      pdf.setFontSize(16);
      pdf.setTextColor(26, 26, 26);
      pdf.text("RAPPORT D'INTERVENTION", pageWidth / 2, y, { align: 'center' });
      y += 12;

      pdf.setFontSize(10);
      pdf.setTextColor(107, 114, 128);
      pdf.text(`Date: ${new Date(data.created_at).toLocaleDateString('fr-FR')}`, margin, y);
      pdf.text(`Technicien: ${data.technician_name}`, pageWidth - margin, y, { align: 'right' });
      y += 10;

      pdf.setDrawColor(139, 115, 85);
      pdf.line(margin, y, pageWidth - margin, y);
      y += 10;

      const addSection = (title, content) => {
        if (y > 270) { pdf.addPage(); y = 20; }
        pdf.setFontSize(12);
        pdf.setTextColor(139, 115, 85);
        pdf.text(title, margin, y);
        y += 6;
        pdf.setFontSize(10);
        pdf.setTextColor(26, 26, 26);
        const lines = pdf.splitTextToSize(content || 'N/A', pageWidth - 2 * margin);
        pdf.text(lines, margin, y);
        y += lines.length * 5 + 8;
      };

      addSection('ADRESSE', data.adresse_complete);
      addSection('TYPE', data.type_batiment);
      addSection('INTERVENTION', (data.type_intervention || []).join(', '));
      addSection('SINISTRÉ', `${data.sinistre_statut}${data.nom_sinistre ? ' - ' + data.nom_sinistre : ''}`);
      if (data.franchise) addSection('FRANCHISE À RÉCUPÉRER', data.franchise_montant || 'Oui');
      if (data.resume_constats) addSection('RÉSUMÉ', data.resume_constats);
      if (data.origine_probable) addSection('ORIGINE PROBABLE', data.origine_probable);
      if (data.preconisations) addSection('PRÉCONISATIONS', data.preconisations);
      if (data.points_urgents) addSection('⚠️ POINTS URGENTS', data.points_urgents);
      if (data.rapport_final) addSection('RAPPORT FINAL', data.rapport_final);

      // Note: Les photos sont générées côté serveur avec le "Rapport KARDIF"
      // Ce PDF simple contient uniquement le texte sans les photos pour éviter les erreurs CORS
      pdf.save(`KARDIF_${(data.adresse_complete || 'rapport').substring(0, 30).replace(/[^a-zA-Z0-9]/g, '_')}.pdf`);
      toast.success('PDF téléchargé ! Pour les photos, utilisez "Rapport KARDIF" ou le ZIP.');
    } catch (e) {
      console.error('Erreur PDF:', e);
      toast.error('Erreur PDF - Essayez le ZIP ou le Rapport KARDIF');
    }
    setExporting(false);
  };

  const exportZip = async () => {
    setExporting(true);
    toast.info('Préparation ZIP... Téléchargement des photos en cours');
    try {
      const zip = new JSZip();
      
      let txt = `KARDIF Investigation - Rapport\n${'='.repeat(50)}\n\n`;
      txt += `Adresse: ${data.adresse_complete}\nDate: ${new Date(data.created_at).toLocaleDateString('fr-FR')}\n`;
      txt += `Technicien: ${data.technician_name}\nClient: ${data.client_nom || 'N/A'}\n\n`;
      if (data.franchise) txt += `FRANCHISE: ${data.franchise_montant || 'À récupérer'}\n\n`;
      txt += `Résumé:\n${data.resume_constats || 'N/A'}\n\nOrigine:\n${data.origine_probable || 'N/A'}\n\n`;
      txt += `Préconisations:\n${data.preconisations || 'N/A'}\n\nRapport final:\n${data.rapport_final || 'N/A'}`;
      
      zip.file('rapport.txt', txt);

      const photos = zip.folder('photos');
      
      // Fonction pour télécharger et ajouter une image (URL ou base64)
      const addPhoto = async (url, name) => {
        if (!url) return;
        try {
          if (url.startsWith('data:')) {
            // Image en base64
            photos.file(name, url.split(',')[1], { base64: true });
          } else if (url.startsWith('http')) {
            // Image URL - télécharger
            const response = await fetch(url);
            const blob = await response.blob();
            photos.file(name, blob);
          }
        } catch (e) {
          console.error('Erreur téléchargement image:', name, e);
        }
      };

      // Télécharger toutes les photos
      if (data.photo_facade) await addPhoto(data.photo_facade, '01_facade.jpg');
      
      for (let i = 0; i < (data.photos_desordres || []).length; i++) {
        await addPhoto(data.photos_desordres[i], `02_desordre_${String(i+1).padStart(2,'0')}.jpg`);
      }
      
      // Photos des mesures d'humidité
      let humCount = 1;
      for (const m of (data.mesures_humidite || [])) {
        for (const p of (m.photos || [])) {
          await addPhoto(p, `03_humidite_${String(humCount++).padStart(2,'0')}.jpg`);
        }
      }
      
      // Photos des mesures thermiques
      let thermoCount = 1;
      for (const m of (data.mesures_thermiques || [])) {
        for (const p of (m.photos || [])) {
          await addPhoto(p, `04_thermique_${String(thermoCount++).padStart(2,'0')}.jpg`);
        }
      }
      
      // Photos des procédés
      let procCount = 1;
      for (const proc of (data.procedes || [])) {
        for (const p of (proc.photos || [])) {
          await addPhoto(p, `05_procede_${String(procCount++).padStart(2,'0')}.jpg`);
        }
      }

      // Anciens formats de photos
      let oldHumCount = 1;
      for (const p of (data.photos_mesures_humidite || [])) {
        await addPhoto(p, `03_humidite_old_${String(oldHumCount++).padStart(2,'0')}.jpg`);
      }
      let oldThermoCount = 1;
      for (const p of (data.photos_camera_thermique || [])) {
        await addPhoto(p, `04_thermique_old_${String(oldThermoCount++).padStart(2,'0')}.jpg`);
      }
      let oldProcCount = 1;
      for (const p of (data.photos_procedes || [])) {
        await addPhoto(p, `05_procede_old_${String(oldProcCount++).padStart(2,'0')}.jpg`);
      }

      // Signatures
      const sigs = zip.folder('signatures');
      if (data.client_signature) {
        try {
          if (data.client_signature.startsWith('data:')) {
            sigs.file('signature_client.png', data.client_signature.split(',')[1], { base64: true });
          }
        } catch (e) {}
      }

      // Documents complémentaires ajoutés par le technicien
      if (data.documents_finaux && data.documents_finaux.length > 0) {
        const docsFolder = zip.folder('documents_complementaires');
        for (let i = 0; i < data.documents_finaux.length; i++) {
          const doc = data.documents_finaux[i];
          if (doc.url && doc.url.startsWith('data:')) {
            const base64 = doc.url.split(',')[1];
            const safeName = (doc.name || `document_${i+1}`).replace(/[^a-zA-Z0-9._-]/g, '_');
            docsFolder.file(`${String(i+1).padStart(2,'0')}_${safeName}`, base64, { base64: true });
          }
        }
      }

      // Rapport PDF final (mise en page auto) + PDF signés annexés
      try {
        const rep = await axios.post(`${API}/interventions/${id}/generate-report?format=pdf`, buildReportBody(), { responseType: 'blob' });
        zip.file('compte_rendu.pdf', rep.data);
      } catch (e) { console.error('Erreur ajout PDF au ZIP:', e); }

      const signedFolder = zip.folder('documents_pdf');
      for (const a of (data.pieces_jointes || [])) {
        if (!a.url) continue;
        try {
          const r = await fetch(fileHref(a.url));
          const blob = await r.blob();
          let fname = (a.name || 'document').replace(/[^a-zA-Z0-9._-]/g, '_');
          if (!fname.toLowerCase().endsWith('.pdf')) fname += '.pdf';
          signedFolder.file(fname, blob);
        } catch (e) { console.error('Erreur ajout PDF joint au ZIP:', e); }
      }

      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `KARDIF_${(data.adresse_complete || 'rapport').substring(0, 30).replace(/[^a-zA-Z0-9]/g, '_')}.zip`);
      toast.success('ZIP téléchargé (rapport PDF + photos + documents) !');
    } catch (e) {
      console.error('Erreur ZIP:', e);
      toast.error('Erreur ZIP');
    }
    setExporting(false);
  };

  const deleteIntervention = async () => {
    if (!window.confirm('Supprimer définitivement cette intervention ? Cette action est irréversible.')) return;
    try {
      await axios.delete(`${API}/interventions/${id}`);
      toast.success('Intervention supprimée');
      nav(user?.role === 'admin' ? '/admin' : user?.role === 'secretary' ? '/secretary' : '/dashboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  if (loading) return <MascotLoader text="Chargement de l'intervention..." />;
  if (!data) return null;

  const isAdmin = user?.role === 'admin';
  const isTechnician = user?.role === 'technician';
  const isSecretary = user?.role === 'secretary';
  const st = { submitted: ['bg-amber-100', 'text-amber-800', 'En attente'], validated: ['bg-green-100', 'text-green-800', 'Validé'], rejected: ['bg-red-100', 'text-red-800', 'Rejeté'] }[data.status] || ['bg-gray-100', 'text-gray-800', data.status];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F9FA] via-white to-[#F5F0EB]" data-testid="intervention-detail">
      {/* Header avec style KARDIF */}
      <header className="bg-gradient-to-r from-[#1A1A1A] via-[#2D2D2D] to-[#1A1A1A] border-b border-[#8B7355]/30 sticky top-0 z-40 no-print">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button variant="ghost" className="text-white hover:bg-[#8B7355]/20" onClick={() => nav(isAdmin ? '/admin' : user?.role === 'secretary' ? '/secretary' : '/dashboard')} data-testid="back-to-dashboard">
            <ArrowLeft className="h-4 w-4 mr-2" />Retour
          </Button>
          <img src={LOGO} alt="KARDIF" className="h-12" />
          <div className="flex items-center gap-2">
          <Button onClick={previewReport} disabled={exporting} variant="outline" className="border-[#8B7355] text-[#8B7355] hover:bg-[#8B7355]/10 hidden sm:inline-flex" data-testid="preview-report-btn">
            <Eye className="h-4 w-4 mr-2" />Aperçu
          </Button>
          <Button onClick={() => downloadReport('pdf')} disabled={exporting} className="bg-[#C5A059] hover:bg-[#D8B468] text-[#1A1A1A] font-bold shadow-lg" data-testid="generate-report-btn">
            <FileDown className="h-4 w-4 mr-2" />{exporting ? 'Génération...' : 'Générer le rapport'}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={exporting} className="border-[#8B7355] text-[#8B7355] hover:bg-[#8B7355] hover:text-white">
                <Download className="h-4 w-4 mr-2" />{exporting ? 'Export...' : 'Exporter'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white border-[#8B7355]/30">
              <DropdownMenuItem onClick={() => downloadReport('pdf')} className="cursor-pointer font-bold text-[#8B7355] hover:bg-[#8B7355]/10">
                <FileDown className="h-4 w-4 mr-2" />📄 Compte-rendu automatique (PDF)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => downloadReport('docx')} className="cursor-pointer font-semibold text-[#8B7355] hover:bg-[#8B7355]/10">
                <FileText className="h-4 w-4 mr-2" />📝 Compte-rendu automatique (Word)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowEmailDialog(true)} className="cursor-pointer font-semibold text-blue-600 hover:bg-blue-50">
                <Mail className="h-4 w-4 mr-2" />📧 Envoyer par email
              </DropdownMenuItem>
              {(isAdmin || user?.role === 'secretary') && (
                <DropdownMenuItem onClick={() => nav(`/factures?intervention=${id}`)} className="cursor-pointer font-semibold text-[#8B7355] hover:bg-[#8B7355]/10" data-testid="create-invoice-from-intervention">
                  <FileText className="h-4 w-4 mr-2" />🧾 Créer une facture
                </DropdownMenuItem>
              )}
              <DropdownMenuItem className="border-t border-[#8B7355]/20 my-1" disabled>─── Options avancées ───</DropdownMenuItem>
              {canEditReport && (
                <DropdownMenuItem onClick={openTextEdit} className="cursor-pointer font-semibold text-emerald-700 hover:bg-emerald-50" data-testid="edit-report-text-btn">
                  <Edit3 className="h-4 w-4 mr-2" />✏️ Corriger le texte (orthographe)
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => nav(`/intervention/${id}/edit-report`)} className="cursor-pointer text-slate-600 hover:bg-slate-100">
                <Edit3 className="h-4 w-4 mr-2" />Personnaliser la mise en page (optionnel)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportPDF} className="cursor-pointer hover:bg-gray-100">
                <FileDown className="h-4 w-4 mr-2" />PDF simple
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportZip} className="cursor-pointer hover:bg-gray-100">
                <Image className="h-4 w-4 mr-2" />ZIP (rapport PDF + photos + documents)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => window.print()} className="cursor-pointer hover:bg-gray-100">
                <FileText className="h-4 w-4 mr-2" />Imprimer
              </DropdownMenuItem>
              {canEditReport && (
                <DropdownMenuItem onClick={deleteIntervention} className="cursor-pointer border-t border-red-100 mt-1 text-red-600 hover:bg-red-50 font-semibold" data-testid="delete-intervention-btn">
                  <Trash2 className="h-4 w-4 mr-2" />Supprimer l'intervention
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* En-tête du compte rendu avec style premium */}
        <div className="mb-8 bg-gradient-to-r from-[#1A1A1A] via-[#2D2D2D] to-[#1A1A1A] rounded-2xl p-6 text-white shadow-xl border border-[#8B7355]/30">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-2 h-8 bg-[#8B7355] rounded-full"></div>
                <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: "'Libre Baskerville', serif" }}>
                  Compte Rendu d'Intervention
                </h1>
              </div>
              <div className="flex items-center gap-4 text-[#D4C5A9] ml-5">
                <span className="flex items-center gap-1"><Calendar className="h-4 w-4" />{new Date(data.created_at).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
                <span className="flex items-center gap-1"><User className="h-4 w-4" />{data.technician_name}</span>
              </div>
              <p className="text-sm text-[#9CA3AF] mt-2 ml-5">
                N° dossier : {data.numero_dossier || data.id?.substring(0, 8)}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <Badge className={`${st[0]} ${st[1]} px-4 py-2 text-sm font-semibold`}>{st[2]}</Badge>
              {(data.edit_history || []).length > 0 && (
                <Badge className="bg-emerald-600 text-white px-3 py-1" data-testid="report-reviewed-badge">
                  <CheckCircle className="h-3 w-3 mr-1" />Rapport corrigé / relu
                </Badge>
              )}
              {data.signed_at && (
                <Badge className="bg-[#8B7355] text-white px-3 py-1">
                  <PenTool className="h-3 w-3 mr-1" />Signé le {new Date(data.signed_at).toLocaleDateString('fr-FR')}
                </Badge>
              )}
              {data.franchise && (
                <Badge className="bg-amber-500 text-white px-3 py-1 animate-pulse">
                  💶 Franchise : {data.franchise_montant || 'À récupérer'}
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Admin/Secrétaire actions */}
        {(isAdmin || isSecretary) && data.status === 'submitted' && (
          <Card className="mb-8 border-2 border-amber-400 bg-gradient-to-r from-amber-50 to-amber-100 no-print shadow-lg">
            <CardContent className="p-6 flex flex-wrap gap-4 items-center">
              <p className="text-sm text-amber-900 font-medium w-full sm:w-auto sm:mr-auto">Relisez et corrigez l'orthographe (menu Exporter → Options avancées) avant de valider.</p>
              <Button onClick={openTextEdit} variant="outline" className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 shadow-sm" data-testid="review-correct-btn">
                <Edit3 className="h-4 w-4 mr-2" />Relire & corriger l'orthographe
              </Button>
              <Button onClick={() => updateStatus('validated')} disabled={updating} className="bg-green-600 hover:bg-green-700 text-white shadow-md" data-testid="validate-report-btn">
                <CheckCircle className="h-4 w-4 mr-2" />Valider ce rapport
              </Button>
              <Button onClick={() => updateStatus('rejected')} disabled={updating} variant="destructive" className="shadow-md">
                <XCircle className="h-4 w-4 mr-2" />Rejeter
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Signature for technician */}
        {isTechnician && !data.client_signature && (
          <Card className="mb-8 border-2 border-blue-400 bg-gradient-to-r from-blue-50 to-blue-100 no-print shadow-lg">
            <CardContent className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2 text-blue-800">
                <PenTool className="h-5 w-5" />
                Signature client requise
              </h3>
              <Dialog open={showSignature} onOpenChange={setShowSignature}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white shadow-md">
                    <PenTool className="h-4 w-4 mr-2" />Faire signer le client
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg bg-white">
                  <DialogHeader>
                    <DialogTitle>Signature du client</DialogTitle>
                  </DialogHeader>
                  <SignaturePad onSave={handleSignature} title="Le client doit signer ci-dessous" />
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        )}

        {/* Send email button */}
        {data.client_signature && (isTechnician || isAdmin) && (
          <Card className="mb-8 border-2 border-green-400 bg-gradient-to-r from-green-50 to-emerald-100 no-print shadow-lg">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-green-800 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5" />
                  Intervention signée avec succès
                </h3>
                <p className="text-sm text-green-700">Vous pouvez envoyer le rapport par email au client</p>
              </div>
              <Button onClick={sendEmail} className="bg-green-600 hover:bg-green-700 text-white shadow-md">
                <Send className="h-4 w-4 mr-2" />Envoyer par email
              </Button>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {/* Photo façade avec style amélioré */}
            {data.photo_facade && (
              <Card className="overflow-hidden shadow-lg border-[#8B7355]/20">
                <div className="bg-gradient-to-r from-[#8B7355] to-[#A08060] p-4">
                  <CardTitle className="flex items-center gap-2 text-white">
                    <Camera className="h-5 w-5" />Photo façade
                  </CardTitle>
                </div>
                <CardContent className="p-4 bg-white">
                  <div className="relative">
                    <img src={data.photo_facade} alt="Façade" className="w-full max-h-[450px] object-contain rounded-lg border-2 border-[#D4C5A9] shadow-md bg-gray-50" />
                    {canEditReport && (
                      <button type="button" onClick={editFacade} data-testid="detail-facade-edit-btn" className="absolute top-3 right-3 bg-[#8B7355] text-white rounded-md px-3 py-1.5 text-xs font-semibold shadow hover:bg-[#7A6548] flex items-center gap-1">
                        <Crop className="h-3.5 w-3.5" />Éditer
                      </button>
                    )}
                    {data.heure_debut && (
                      <span className="absolute bottom-3 right-3 bg-[#1A1A1A]/80 text-white text-sm px-4 py-2 rounded-full font-medium">
                        ⏰ Arrivée: {new Date(data.heure_debut).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Lieu et Type intervention */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="shadow-lg border-[#8B7355]/20 overflow-hidden">
                <div className="bg-gradient-to-r from-[#8B7355] to-[#A08060] p-3">
                  <CardTitle className="flex items-center gap-2 text-white text-base">
                    <MapPin className="h-4 w-4" />Lieu d'intervention
                  </CardTitle>
                </div>
                <CardContent className="p-4 bg-white">
                  <p className="font-semibold text-[#1A1A1A] text-lg">{data.adresse_complete}</p>
                  <Badge className="mt-2 bg-[#D4C5A9] text-[#1A1A1A]">{data.type_batiment}</Badge>
                </CardContent>
              </Card>

              <Card className="shadow-lg border-[#8B7355]/20 overflow-hidden">
                <div className="bg-gradient-to-r from-[#8B7355] to-[#A08060] p-3">
                  <CardTitle className="flex items-center gap-2 text-white text-base">
                    <Building className="h-4 w-4" />Type d'intervention
                  </CardTitle>
                </div>
                <CardContent className="p-4 bg-white">
                  <div className="flex flex-wrap gap-2">
                    {(data.type_intervention || []).map((t, i) => (
                      <Badge key={i} className="bg-[#1A1A1A] text-white px-3 py-1">{t}</Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sinistré */}
            <Card className="shadow-lg border-[#8B7355]/20 overflow-hidden">
              <div className="bg-gradient-to-r from-[#8B7355] to-[#A08060] p-3">
                <CardTitle className="flex items-center gap-2 text-white text-base">
                  <Users className="h-4 w-4" />Informations sinistré / client
                </CardTitle>
              </div>
              <CardContent className="p-4 bg-white">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-[#F8F9FA] rounded-lg">
                    <p className="text-xs text-[#6B7280] uppercase tracking-wide">Statut</p>
                    <p className="font-semibold capitalize text-[#1A1A1A]">{data.sinistre_statut}</p>
                  </div>
                  {data.nom_sinistre && (
                    <div className="p-3 bg-[#F8F9FA] rounded-lg">
                      <p className="text-xs text-[#6B7280] uppercase tracking-wide">Nom sinistré</p>
                      <p className="font-semibold text-[#1A1A1A]">{data.nom_sinistre}</p>
                    </div>
                  )}
                  {data.client_nom && (
                    <div className="p-3 bg-[#F8F9FA] rounded-lg">
                      <p className="text-xs text-[#6B7280] uppercase tracking-wide">Client</p>
                      <p className="font-semibold text-[#1A1A1A]">{data.client_nom}</p>
                    </div>
                  )}
                  {data.client_telephone && (
                    <div className="p-3 bg-[#F8F9FA] rounded-lg">
                      <p className="text-xs text-[#6B7280] uppercase tracking-wide">Téléphone</p>
                      <p className="font-semibold text-[#8B7355]">{data.client_telephone}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Pièces sinistrées */}
            {data.pieces_sinistrees?.length > 0 && (
              <Card className="shadow-lg border-[#8B7355]/20 overflow-hidden">
                <div className="bg-gradient-to-r from-[#8B7355] to-[#A08060] p-3">
                  <CardTitle className="flex items-center gap-2 text-white text-base">
                    <Home className="h-4 w-4" />Pièces sinistrées ({data.pieces_sinistrees.length})
                  </CardTitle>
                </div>
                <CardContent className="p-4 bg-white space-y-3">
                  {data.pieces_sinistrees.map((p, i) => (
                    <div key={i} className="p-4 bg-gradient-to-r from-[#F8F9FA] to-[#F5F0EB] rounded-lg border-l-4 border-[#8B7355]">
                      <p className="font-semibold text-[#1A1A1A]">{p.piece}</p>
                      {p.stigmates?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {p.stigmates.map((s, j) => (
                            <Badge key={j} variant="outline" className="text-xs border-[#8B7355] text-[#8B7355]">{s}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {data.constats_arrivee && (
              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2"><Eye className="h-5 w-5 text-[#8B7355]" />Constats</CardTitle></CardHeader>
                <CardContent><p className="whitespace-pre-wrap">{data.constats_arrivee}</p></CardContent>
              </Card>
            )}

            {/* Photos désordres avec style amélioré */}
            {data.photos_desordres?.length > 0 && (
              <Card className="shadow-lg border-[#8B7355]/20 overflow-hidden">
                <div className="bg-gradient-to-r from-[#8B7355] to-[#A08060] p-3">
                  <CardTitle className="flex items-center gap-2 text-white text-base">
                    <Camera className="h-4 w-4" />Photos des désordres ({data.photos_desordres.length})
                  </CardTitle>
                </div>
                <CardContent className="p-4 bg-white">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {data.photos_desordres.map((p, i) => (
                      <div key={i} className="relative group overflow-hidden rounded-lg border-2 border-[#D4C5A9] shadow-md">
                        <img src={p} alt={`Désordre ${i+1}`} className="w-full h-40 object-cover transition-transform group-hover:scale-105" />
                        <span className="absolute bottom-2 left-2 bg-[#1A1A1A]/80 text-white text-xs px-2 py-1 rounded-full font-medium">
                          #{i + 1}
                        </span>
                        {canEditReport && (
                          <button type="button" onClick={() => editDesordre(i)} data-testid={`detail-desordre-edit-btn-${i}`} className="absolute top-2 right-2 bg-[#8B7355] text-white rounded-md px-2 py-1 text-[10px] font-semibold shadow hover:bg-[#7A6548] flex items-center gap-1">
                            <Crop className="h-3 w-3" />Éditer
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Mesures d'humidité avec photos */}
            {(data.mesures_humidite || []).some(m => m.zone || m.valeur || m.photos?.length > 0) && (
              <Card className="shadow-lg border-[#8B7355]/20 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-blue-500 p-3">
                  <CardTitle className="flex items-center gap-2 text-white text-base">
                    <Droplets className="h-4 w-4" />Mesures d'humidité
                  </CardTitle>
                </div>
                <CardContent className="p-4 bg-white space-y-4">
                  {(data.mesures_humidite || []).filter(m => m.zone || m.valeur || m.photos?.length > 0).map((m, i) => (
                    <div key={i} className="p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border-l-4 border-blue-500">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-semibold text-[#1A1A1A]">{m.zone || `Zone ${i + 1}`}</p>
                          {m.support && <p className="text-sm text-[#6B7280]">Support: {m.support}</p>}
                        </div>
                        {m.valeur && (
                          <div className="text-right bg-white px-4 py-2 rounded-lg shadow-sm">
                            <p className="text-2xl font-bold text-blue-600">{m.valeur}</p>
                            <p className="text-xs text-[#6B7280]">% HR</p>
                          </div>
                        )}
                      </div>
                      {m.photos?.length > 0 && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
                          {m.photos.map((photo, j) => (
                            <div key={j} className="relative">
                              <img src={photo} alt={`Humidité ${i+1}-${j+1}`} className="w-full h-32 object-cover rounded-lg border-2 border-blue-200 shadow-sm" />
                              {canEditReport && (
                                <button type="button" onClick={() => editNestedPhoto('mesures_humidite', m, j)} className="absolute top-1 right-1 bg-[#8B7355] text-white rounded px-1.5 py-0.5 text-[9px] font-semibold shadow hover:bg-[#7A6548] flex items-center gap-0.5"><Crop className="h-2.5 w-2.5" />Éditer</button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Mesures thermiques avec photos */}
            {(data.mesures_thermiques || []).some(m => m.zone || m.observation || m.photos?.length > 0) && (
              <Card className="shadow-lg border-[#8B7355]/20 overflow-hidden">
                <div className="bg-gradient-to-r from-orange-600 to-red-500 p-3">
                  <CardTitle className="flex items-center gap-2 text-white text-base">
                    🌡️ Mesures thermiques
                  </CardTitle>
                </div>
                <CardContent className="p-4 bg-white space-y-4">
                  {(data.mesures_thermiques || []).filter(m => m.zone || m.observation || m.photos?.length > 0).map((m, i) => (
                    <div key={i} className="p-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg border-l-4 border-orange-500">
                      <div className="mb-3">
                        <p className="font-semibold text-[#1A1A1A]">{m.zone || `Zone ${i + 1}`}</p>
                        {m.observation && <p className="text-sm text-[#6B7280] mt-1">{m.observation}</p>}
                      </div>
                      {m.photos?.length > 0 && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {m.photos.map((photo, j) => (
                            <div key={j} className="relative">
                              <img src={photo} alt={`Thermique ${i+1}-${j+1}`} className="w-full h-32 object-cover rounded-lg border-2 border-orange-200 shadow-sm" />
                              {canEditReport && (
                                <button type="button" onClick={() => editNestedPhoto('mesures_thermiques', m, j)} className="absolute top-1 right-1 bg-[#8B7355] text-white rounded px-1.5 py-0.5 text-[9px] font-semibold shadow hover:bg-[#7A6548] flex items-center gap-0.5"><Crop className="h-2.5 w-2.5" />Éditer</button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Photos humidité (ancien format) */}
            {data.photos_mesures_humidite?.length > 0 && (
              <Card className="shadow-lg border-[#8B7355]/20 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-blue-500 p-3">
                  <CardTitle className="flex items-center gap-2 text-white text-base">
                    <Droplets className="h-4 w-4" />Photos mesures d'humidité
                  </CardTitle>
                </div>
                <CardContent className="p-4 bg-white">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {data.photos_mesures_humidite.map((p, i) => (
                      <div key={i} className="relative overflow-hidden rounded-lg border-2 border-blue-200 shadow-md">
                        <img src={p} alt={`Humidité ${i+1}`} className="w-full h-40 object-cover" />
                        <span className="absolute bottom-2 left-2 bg-blue-600/90 text-white text-xs px-2 py-1 rounded-full font-medium">
                          💧 #{i + 1}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Photos thermiques (ancien format) */}
            {data.photos_camera_thermique?.length > 0 && (
              <Card className="shadow-lg border-[#8B7355]/20 overflow-hidden">
                <div className="bg-gradient-to-r from-orange-600 to-red-500 p-3">
                  <CardTitle className="flex items-center gap-2 text-white text-base">
                    🌡️ Photos caméra thermique
                  </CardTitle>
                </div>
                <CardContent className="p-4 bg-white">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {data.photos_camera_thermique.map((p, i) => (
                      <div key={i} className="relative overflow-hidden rounded-lg border-2 border-orange-200 shadow-md">
                        <img src={p} alt={`Thermique ${i+1}`} className="w-full h-40 object-cover" />
                        <span className="absolute bottom-2 left-2 bg-orange-600/90 text-white text-xs px-2 py-1 rounded-full font-medium">
                          🔥 #{i + 1}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Photos procédés (ancien format) */}
            {data.photos_procedes?.length > 0 && (
              <Card className="shadow-lg border-[#8B7355]/20 overflow-hidden">
                <div className="bg-gradient-to-r from-green-600 to-emerald-500 p-3">
                  <CardTitle className="flex items-center gap-2 text-white text-base">
                    <Wrench className="h-4 w-4" />Photos des procédés
                  </CardTitle>
                </div>
                <CardContent className="p-4 bg-white">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {data.photos_procedes.map((p, i) => (
                      <div key={i} className="relative overflow-hidden rounded-lg border-2 border-green-200 shadow-md">
                        <img src={p} alt={`Procédé ${i+1}`} className="w-full h-40 object-cover" />
                        <span className="absolute bottom-2 left-2 bg-green-600/90 text-white text-xs px-2 py-1 rounded-full font-medium">
                          🔧 #{i + 1}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Procédés avec photos */}
            {(data.procedes_effectues?.length > 0 || (data.procedes && data.procedes.length > 0)) && (
              <Card className="shadow-lg border-[#8B7355]/20 overflow-hidden">
                <div className="bg-gradient-to-r from-green-600 to-emerald-500 p-3">
                  <CardTitle className="flex items-center gap-2 text-white text-base">
                    <Wrench className="h-4 w-4" />Procédés effectués
                  </CardTitle>
                </div>
                <CardContent className="p-4 bg-white space-y-4">
                  {data.procedes_effectues?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {data.procedes_effectues.map((p, i) => (
                        <Badge key={i} className="bg-[#1A1A1A] text-white px-3 py-1">{p}</Badge>
                      ))}
                    </div>
                  )}
                  {data.procedes?.filter(p => p.type || p.description || p.photos?.length > 0).map((p, i) => (
                    <div key={i} className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-l-4 border-green-500">
                      <div className="mb-3">
                        <p className="font-semibold text-[#1A1A1A] text-lg">{p.nom || p.type || `Procédé ${i + 1}`}</p>
                        {p.description && <p className="text-sm text-[#6B7280] mt-1">{p.description}</p>}
                        {p.resultat && (
                          <div className="mt-2 p-2 bg-white rounded border border-green-200">
                            <span className="font-medium text-green-700">✓ Résultat:</span>
                            <span className="ml-2 text-[#1A1A1A]">{p.resultat}</span>
                          </div>
                        )}
                      </div>
                      {p.photos?.length > 0 && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {p.photos.map((photo, j) => (
                            <div key={j} className="relative">
                              <img src={photo} alt={`Procédé ${i+1}-${j+1}`} className="w-full h-32 object-cover rounded-lg border-2 border-green-200 shadow-sm" />
                              {canEditReport && (
                                <button type="button" onClick={() => editNestedPhoto('procedes', p, j)} className="absolute top-1 right-1 bg-[#8B7355] text-white rounded px-1.5 py-0.5 text-[9px] font-semibold shadow hover:bg-[#7A6548] flex items-center gap-0.5"><Crop className="h-2.5 w-2.5" />Éditer</button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  {data.notes_procedes && (
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm font-medium text-blue-800 mb-1">📝 Notes:</p>
                      <p className="text-blue-700 whitespace-pre-wrap">{data.notes_procedes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {(data.resume_constats || data.origine_probable || data.preconisations || data.points_urgents || data.notes_conclusion) && (
              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-[#8B7355]" />Conclusion</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  {data.resume_constats && <div><p className="text-sm text-[#6B7280]">Résumé</p><p className="whitespace-pre-wrap">{data.resume_constats}</p></div>}
                  {data.origine_probable && <div><p className="text-sm text-[#6B7280]">Origine probable</p><p className="whitespace-pre-wrap">{data.origine_probable}</p></div>}
                  {data.preconisations && <div><p className="text-sm text-[#6B7280]">Préconisations</p><p className="whitespace-pre-wrap">{data.preconisations}</p></div>}
                  {data.notes_conclusion && <div><p className="text-sm text-[#6B7280]">Conclusion</p><p className="whitespace-pre-wrap">{data.notes_conclusion}</p></div>}
                  {data.points_urgents && <div className="p-3 bg-red-50 border border-red-200 rounded-lg"><p className="text-sm font-medium text-red-800">Points urgents</p><p className="text-red-700 whitespace-pre-wrap">{data.points_urgents}</p></div>}
                </CardContent>
              </Card>
            )}

            {/* Signatures display */}
            {(data.client_signature || data.technician_signature) && (
              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2"><PenTool className="h-5 w-5 text-[#8B7355]" />Signatures</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  {data.client_signature && (
                    <div>
                      <p className="text-sm text-[#6B7280] mb-2">Signature client</p>
                      <img src={data.client_signature} alt="Signature client" className="border rounded-lg max-h-32" />
                      {data.signed_at && <p className="text-xs text-[#6B7280] mt-1">Signé le {new Date(data.signed_at).toLocaleString('fr-FR')}</p>}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Documents finaux - Post-intervention */}
            {(isTechnician || isAdmin || user?.role === 'secretary') && (
              <Card className="border-[#8B7355]/30 no-print" data-testid="documents-finaux-section">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FolderOpen className="h-5 w-5 text-[#8B7355]" />
                    Documents finaux
                  </CardTitle>
                  <p className="text-sm text-[#6B7280]">
                    Déposez les documents signés, photos supplémentaires et compte-rendu pour l'extranet
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Documents signés */}
                  <div>
                    <Label className="flex items-center gap-2 mb-3">
                      <FileText className="h-4 w-4" />
                      Documents signés
                    </Label>
                    {data.documents_finaux?.length > 0 && (
                      <div className="space-y-2 mb-3">
                        {data.documents_finaux.map((doc, i) => (
                          <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-[#8B7355]" />
                              <div>
                                <p className="text-sm font-medium">{doc.name}</p>
                                <p className="text-xs text-[#6B7280]">Par {doc.uploaded_by} - {new Date(doc.uploaded_at).toLocaleDateString('fr-FR')}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <a href={fileHref(doc.url)} target="_blank" rel="noreferrer" className="text-[#8B7355] hover:underline" title="Ouvrir">
                                <Eye className="h-4 w-4" />
                              </a>
                              <a href={fileHref(doc.url)} download={doc.name} className="text-[#8B7355] hover:underline" title="Télécharger">
                                <Download className="h-4 w-4" />
                              </a>
                              {(isTechnician || isAdmin || isSecretary) && (
                                <Button variant="ghost" size="sm" onClick={() => removeDocument(i)} className="text-red-500 hover:text-red-700">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {(isTechnician || isAdmin || isSecretary) && (
                      <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-[#8B7355]/30 rounded-lg cursor-pointer hover:bg-[#8B7355]/5 transition-colors">
                        <input
                          type="file"
                          multiple
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          onChange={uploadDocument}
                          className="hidden"
                          disabled={uploadingDoc}
                        />
                        {uploadingDoc ? (
                          <span className="text-sm text-[#6B7280]">Upload en cours...</span>
                        ) : (
                          <>
                            <Upload className="h-5 w-5 text-[#8B7355]" />
                            <span className="text-sm text-[#6B7280]">Ajouter des documents signés</span>
                          </>
                        )}
                      </label>
                    )}
                  </div>

                  {/* Photos finales */}
                  <div>
                    <Label className="flex items-center gap-2 mb-3">
                      <Camera className="h-4 w-4" />
                      Photos supplémentaires
                    </Label>
                    {data.photos_finales?.length > 0 && (
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        {data.photos_finales.map((photo, i) => (
                          <img key={i} src={fileHref(photo)} alt={`Photo ${i + 1}`} className="w-full h-24 object-cover rounded-lg" />
                        ))}
                      </div>
                    )}
                    {(isTechnician || isAdmin || isSecretary) && (
                      <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-[#8B7355]/30 rounded-lg cursor-pointer hover:bg-[#8B7355]/5 transition-colors">
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          capture="environment"
                          onChange={uploadPhotoFinale}
                          className="hidden"
                          disabled={uploadingPhoto}
                        />
                        {uploadingPhoto ? (
                          <span className="text-sm text-[#6B7280]">Upload en cours...</span>
                        ) : (
                          <>
                            <Plus className="h-5 w-5 text-[#8B7355]" />
                            <span className="text-sm text-[#6B7280]">Ajouter des photos</span>
                          </>
                        )}
                      </label>
                    )}
                  </div>

                  {/* Historique des envois par email */}
                  {data.email_history?.length > 0 && (
                    <div data-testid="email-history-section">
                      <Label className="flex items-center gap-2 mb-3">
                        <Send className="h-4 w-4 text-[#8B7355]" />
                        Suivi des envois ({data.email_history.length})
                      </Label>
                      <div className="space-y-2">
                        {[...data.email_history].reverse().map((h, i) => (
                          <div key={i} className="flex items-start gap-3 p-3 rounded-lg border border-[#8B7355]/20 bg-[#8B7355]/5" data-testid={`email-history-item-${i}`}>
                            <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                            <div className="min-w-0 text-sm">
                              <p className="font-semibold text-[#1A1A1A] truncate">{h.sent_to}</p>
                              {h.cc?.length > 0 && <p className="text-xs text-[#6B7280] truncate">Cc : {h.cc.join(', ')}</p>}
                              <p className="text-xs text-[#6B7280]">
                                {new Date(h.sent_at).toLocaleString('fr-FR')} · par {h.sent_by}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}


                  {/* Compte-rendu final */}
                  {(isTechnician || isAdmin) && (
                    <div>
                      <Label className="flex items-center gap-2 mb-3">
                        <FileText className="h-4 w-4" />
                        Compte-rendu final
                      </Label>
                      <Textarea
                        value={compteRenduFinal}
                        onChange={(e) => setCompteRenduFinal(e.target.value)}
                        placeholder="Notes complémentaires, observations finales pour l'extranet..."
                        rows={4}
                        className="mb-3"
                      />
                      <div className="flex gap-2">
                        <Button 
                          onClick={() => saveCompteRendu(false)} 
                          disabled={updating}
                          className="flex-1 bg-[#8B7355] hover:bg-[#7A6548] text-white"
                        >
                          {updating ? 'Enregistrement...' : 'Enregistrer'}
                        </Button>
                        <Button 
                          onClick={() => saveCompteRendu(true)} 
                          disabled={updating}
                          variant="outline"
                          className="flex-1"
                        >
                          <Mail className="h-4 w-4 mr-2" />
                          Notifier secrétaire
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Display compte-rendu for secretary */}
                  {user?.role === 'secretary' && data.compte_rendu_final && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm font-medium text-green-800 mb-2">Compte-rendu du technicien</p>
                      <p className="text-green-700 whitespace-pre-wrap">{data.compte_rendu_final}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-4">
              {/* Franchise box */}
              {(isAdmin || data.franchise) && (
                <Card className={data.franchise ? 'border-amber-300 bg-amber-50' : ''}>
                  <CardHeader><CardTitle className="flex items-center gap-2 text-amber-700">💰 Franchise</CardTitle></CardHeader>
                  <CardContent>
                    {isAdmin && !data.franchise ? (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Checkbox checked={franchise} onCheckedChange={setFranchise} />
                          <Label>Franchise à récupérer</Label>
                        </div>
                        {franchise && (
                          <Input
                            placeholder="Montant (ex: 150€)"
                            value={franchiseMontant}
                            onChange={(e) => setFranchiseMontant(e.target.value)}
                          />
                        )}
                      </div>
                    ) : data.franchise ? (
                      <p className="font-semibold text-amber-800">Montant: {data.franchise_montant || 'À déterminer'}</p>
                    ) : (
                      <p className="text-[#6B7280]">Pas de franchise</p>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Admin rapport final */}
              {isAdmin && (
                <Card className="no-print">
                  <CardHeader><CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-[#8B7355]" />Rapport final</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Rédigez le rapport final</Label>
                      <Textarea value={rapport} onChange={(e) => setRapport(e.target.value)} className="mt-2" rows={10} />
                    </div>
                    <div className="space-y-2">
                      <Button onClick={() => saveRapport(false)} disabled={updating} className="w-full bg-[#8B7355] hover:bg-[#7A6548] text-white">
                        {updating ? 'Enregistrement...' : 'Enregistrer'}
                      </Button>
                      <Button onClick={() => saveRapport(true)} disabled={updating} variant="outline" className="w-full">
                        <Mail className="h-4 w-4 mr-2" />
                        Enregistrer et notifier secrétaire
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Validated rapport display */}
              {data.rapport_final && (
                <Card className="border-green-300 bg-green-50">
                  <CardHeader><CardTitle className="flex items-center gap-2 text-green-700"><CheckCircle className="h-5 w-5" />Rapport validé</CardTitle></CardHeader>
                  <CardContent><p className="whitespace-pre-wrap">{data.rapport_final}</p></CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Pièces jointes (PDF) */}
      <div className="max-w-6xl mx-auto px-4 pb-10 no-print">
        <div className="bg-white rounded-xl border border-[#8B7355]/20 shadow-sm p-5" data-testid="attachments-section">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <h3 className="text-lg font-bold text-[#8B7355] flex items-center gap-2"><FolderOpen className="h-5 w-5" />Pièces jointes (PDF)</h3>
              <p className="text-xs text-[#6B7280] mt-0.5">Accessibles à tout moment par le technicien, l'admin et la secrétaire. Ouvrez, téléchargez, signez ou écrivez dessus. Cochez les documents à annexer au rapport final.</p>
            </div>
            <label className="cursor-pointer">
              <input type="file" accept="application/pdf,.pdf" className="hidden" data-testid="attachment-file-input"
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; uploadAttachment(f); }} disabled={uploadingAttach} />
              <span className="inline-flex items-center gap-2 bg-[#8B7355] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#6f5c43]">
                <Upload className="h-4 w-4" />{uploadingAttach ? 'Envoi...' : 'Ajouter un PDF'}
              </span>
            </label>
          </div>
          {(data.pieces_jointes || []).length === 0 ? (
            <p className="text-sm text-[#9CA3AF]">Aucune pièce jointe. Ajoutez un document PDF (ex : attestation à faire signer par le client).</p>
          ) : (
            <ul className="space-y-2">
              {(data.pieces_jointes || []).map((a) => (
                <li key={a.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-[#E5E0D8] bg-[#F8F5F0]" data-testid="attachment-item">
                  <div className="flex items-center gap-3 min-w-0">
                    {a.url && (
                      <label className="flex items-center gap-1.5 flex-shrink-0 cursor-pointer" title="Inclure ce document à la fin du rapport">
                        <input
                          type="checkbox"
                          checked={!excludedDocIds.includes(a.id)}
                          onChange={(e) => setExcludedDocIds((prev) => e.target.checked ? prev.filter((x) => x !== a.id) : [...prev, a.id])}
                          className="h-4 w-4 accent-[#8B7355]"
                          data-testid="attachment-include-checkbox"
                        />
                      </label>
                    )}
                    <FileText className="h-5 w-5 text-red-600 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium text-[#1A1A1A] truncate">{a.name}{a.signed && <span className="ml-2 text-xs text-emerald-700 font-semibold">✓ signé</span>}</p>
                      <p className="text-xs text-[#6B7280]">Ajouté par {a.uploaded_by_name || '—'}{a.size ? ` · ${Math.round(a.size / 1024)} Ko` : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <a href={fileHref(a.url)} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 border border-[#8B7355] text-[#8B7355] px-3 py-1.5 rounded-lg text-sm hover:bg-[#8B7355]/10" data-testid="attachment-open-btn">
                      <Eye className="h-4 w-4" />Ouvrir
                    </a>
                    <a href={fileHref(a.url)} download={a.name}
                      className="inline-flex items-center gap-1 border border-[#8B7355] text-[#8B7355] px-3 py-1.5 rounded-lg text-sm hover:bg-[#8B7355]/10" data-testid="attachment-download-btn">
                      <Download className="h-4 w-4" />Télécharger
                    </a>
                    <button onClick={() => { setSignAtt(a); setSigPreview(null); setTimeout(sigClear, 100); }} className="inline-flex items-center gap-1 border border-emerald-500 text-emerald-700 px-3 py-1.5 rounded-lg text-sm hover:bg-emerald-50" data-testid="attachment-sign-btn">
                      <Edit3 className="h-4 w-4" />Signer / Écrire
                    </button>
                    {(canEditReport || a.uploaded_by === user?.id) && (
                      <button onClick={() => deleteAttachment(a.id)} className="text-red-500 hover:text-red-700 p-1.5" data-testid="attachment-delete-btn"><Trash2 className="h-4 w-4" /></button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {(data.edit_history || []).length > 0 && (
            <div className="mt-5 pt-4 border-t border-[#E5E0D8]">
              <p className="text-sm font-semibold text-[#8B7355] mb-2">Historique des corrections</p>
              <ul className="space-y-1" data-testid="edit-history-list">
                {(data.edit_history || []).slice().reverse().map((h) => (
                  <li key={h.id} className="text-xs text-[#6B7280]" data-testid="edit-history-item">
                    <span className="font-medium text-[#333]">{h.user_name}</span> ({h.user_role}) — {new Date(h.at).toLocaleString('fr-FR')} · {(h.fields || []).join(', ')}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Dialogue signature d'une pièce jointe */}
      <Dialog open={!!signAtt} onOpenChange={(o) => { if (!o) { setSignAtt(null); setSigPreview(null); } }}>
        <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader><DialogTitle className="text-[#8B7355]">Signer / Écrire sur le PDF — {signAtt?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder="Nom du signataire (optionnel)" className="w-full border rounded px-3 py-2 text-sm" data-testid="signer-name-input" />
            <div>
              <label className="text-xs font-semibold text-[#8B7355] block mb-1">1. Dessinez la signature (optionnel)</label>
              <div className="border-2 border-dashed border-[#8B7355]/40 rounded-lg bg-white">
                <canvas ref={sigCanvasRef} width={520} height={180} className="w-full touch-none cursor-crosshair"
                  onPointerDown={sigStart} onPointerMove={sigMove} onPointerUp={sigEnd} onPointerLeave={sigEnd} data-testid="signature-canvas" />
              </div>
              <div className="flex justify-end mt-1">
                <Button variant="outline" size="sm" onClick={sigClear} data-testid="signature-clear-btn">Effacer</Button>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#8B7355] block mb-1">2. Placez la signature et/ou ajoutez du texte (date, franchise…) sur le document</label>
              {signAtt && (
                <PdfSignPlacer
                  pdfUrl={fileHref(signAtt.url)}
                  signatureUrl={sigPreview}
                  onChange={setSignPlacement}
                />
              )}
            </div>
            <div className="flex justify-end pt-1">
              <Button onClick={submitSignature} disabled={signing} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="signature-save-btn">{signing ? 'Enregistrement...' : 'Valider'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showTextEdit} onOpenChange={setShowTextEdit}>
        <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto bg-white">
          <DialogHeader><DialogTitle className="text-[#8B7355]">Corriger le texte du rapport</DialogTitle></DialogHeader>
          {editForm && (
            <div className="space-y-4">
              {[['resume_constats', 'Constats / Résumé'], ['origine_probable', 'Origine probable'], ['preconisations', 'Préconisations'], ['points_urgents', 'Points urgents'], ['notes_conclusion', 'Conclusion']].map(([k, label]) => (
                <div key={k}>
                  <label className="text-sm font-semibold text-[#8B7355]">{label}</label>
                  <Textarea value={editForm[k]} onChange={(e) => setEditForm(f => ({ ...f, [k]: e.target.value }))} rows={3} className="mt-1" data-testid={`edit-${k}`} />
                </div>
              ))}
              {editForm.procedes.length > 0 && <p className="text-sm font-bold text-[#8B7355] pt-2 border-t">Procédés</p>}
              {editForm.procedes.map((p, i) => (
                <div key={i} className="p-3 rounded-lg border border-[#E5E0D8] space-y-2">
                  <input value={p.nom || p.type || ''} onChange={(e) => setEditForm(f => { const pr = [...f.procedes]; pr[i] = { ...pr[i], nom: e.target.value }; return { ...f, procedes: pr }; })} className="w-full font-semibold border rounded px-2 py-1 text-sm" placeholder="Nom du procédé" data-testid={`edit-procede-nom-${i}`} />
                  <Textarea value={p.description || ''} onChange={(e) => setEditForm(f => { const pr = [...f.procedes]; pr[i] = { ...pr[i], description: e.target.value }; return { ...f, procedes: pr }; })} rows={2} placeholder="Description" data-testid={`edit-procede-desc-${i}`} />
                  <Textarea value={p.resultat || ''} onChange={(e) => setEditForm(f => { const pr = [...f.procedes]; pr[i] = { ...pr[i], resultat: e.target.value }; return { ...f, procedes: pr }; })} rows={2} placeholder="Résultat" data-testid={`edit-procede-res-${i}`} />
                </div>
              ))}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowTextEdit(false)}>Annuler</Button>
                <Button onClick={saveText} disabled={savingText} className="bg-[#8B7355] text-white" data-testid="save-report-text-btn">{savingText ? 'Enregistrement...' : 'Enregistrer les corrections'}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Dialogue envoi email */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#8B7355]">
              <Mail className="h-5 w-5" />
              Envoyer le rapport par email
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email-to">Destinataire *</Label>
              <Input
                id="email-to"
                type="email"
                placeholder="client@example.com"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                className="border-[#8B7355]/30 focus:border-[#8B7355]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email-cc">Copie (CC) - optionnel</Label>
              <Input
                id="email-cc"
                type="text"
                placeholder="email1@example.com, email2@example.com"
                value={emailCc}
                onChange={(e) => setEmailCc(e.target.value)}
                className="border-[#8B7355]/30 focus:border-[#8B7355]"
              />
              <p className="text-xs text-gray-500">Séparez les adresses par des virgules</p>
            </div>
            
            <div className="bg-[#F8F5F0] border border-[#8B7355]/20 rounded-lg p-4 space-y-2">
              <p className="text-sm font-medium text-[#8B7355]">Le rapport envoyé contiendra :</p>
              <ul className="text-sm text-gray-600 space-y-1 ml-4">
                <li>• Le compte-rendu complet en PDF</li>
                <li>• Toutes les photos de l'intervention</li>
                <li>• Les mesures et procédés effectués</li>
                <li>• La signature du client (si présente)</li>
              </ul>
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowEmailDialog(false)}>
                Annuler
              </Button>
              <Button 
                onClick={sendReportByEmail} 
                disabled={sendingEmail || !emailTo.trim()}
                className="bg-[#8B7355] hover:bg-[#7A6548] text-white"
              >
                {sendingEmail ? (
                  <>Envoi en cours...</>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Envoyer
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <PhotoEditor
        open={photoEditor.open}
        imageSrc={photoEditor.src}
        uploading={editorUploading}
        onClose={() => setPhotoEditor({ open: false, src: null, onSaveUrl: null })}
        onSave={handlePhotoEditorSave}
      />
    </div>
  );
}

export default InterventionDetail;
